<template>
  <div class="video-task-page">
    <h2 class="page-title">视频发布任务</h2>

    <!-- 搜索区域 -->
    <div class="search-bar">
      <el-input v-model="searchForm.task_name" placeholder="请输入任务名称" clearable size="large"
        style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
        <template #prefix>
          <el-icon>
            <Search />
          </el-icon>
        </template>
      </el-input>

      <el-button type="primary" size="large" @click="handleSearch">
        <el-icon>
          <Search />
        </el-icon>
        搜索
      </el-button>

      <el-button type="success" size="large" @click="openAddDialog" class="add-btn">
        <el-icon>
          <Plus />
        </el-icon>
        新增任务
      </el-button>
    </div>

    <!-- 任务列表 -->
    <div class="table-wrapper">
      <el-table :data="taskList" stripe v-loading="loading" class="task-table">
        <el-table-column prop="task_name" label="任务名称" width="180" />
        <el-table-column label="账号" width="150">
          <template #default="{ row }">
            <span>{{ row.account_name || getVideoAccountName(row.video_account_id) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="视频分组" width="150">
          <template #default="{ row }">
            <span>{{ getVideoGroupName(row.video_group_id) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="视频总数" width="140" align="center">
          <template #default="{ row }">
            <el-tag type="info" size="small">{{ row.category_total_videos || 0 }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="已使用视频" width="120" align="center">
          <template #default="{ row }">
            <el-tag type="warning" size="small">{{ row.category_used_videos || 0 }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="今日已发送" width="120" align="center">
          <template #default="{ row }">
            <el-tag type="success" size="small">{{ row.today_tasks || 0 }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="每日发布数" width="120">
          <template #default="{ row }">
            <el-tag type="info">{{ row.daily_maximum_release }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="已发布总数" width="120">
          <template #default="{ row }">
            <el-tag type="warning">{{ row.total_tasks || 0 }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="总发布数" width="120">
          <template #default="{ row }">
            <el-tag type="success">{{ row.total_publications }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="错误信息" width="180">
          <template #default="{ row }">
            <el-tooltip v-if="row.error_message" :content="row.error_message" placement="top">
              <el-tag type="danger" size="small">错误</el-tag>
            </el-tooltip>
            <el-tag v-else type="info" size="small">无错误</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="最后发布时间" width="180">
          <template #default="{ row }">
            <span>{{ formatDateTime(row.last_execution_time) || '未发布' }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column prop="update_time" label="更新时间" width="180" />

        <el-table-column label="目前状态" width="120" fixed="right">
          <template #default="{ row }">
            <el-tag :type="getStatusTagType(row.status)" size="small">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="启动状态" width="120" fixed="right">
          <template #default="{ row }">
            <el-button :type="row.qd_status === 1 ? 'warning' : 'success'" size="small" @click="toggleTaskStatus(row)"
              :loading="row.statusLoading">
              {{ row.qd_status === 1 ? '暂停任务' : '启动任务' }}
            </el-button>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="180" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
              <el-icon>
                <Edit />
              </el-icon>
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
              <el-icon>
                <Delete />
              </el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页组件 -->
    <div class="pagination-container">
      <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
        :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
        @size-change="fetchTaskList" @current-change="fetchTaskList" />
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="900px" :close-on-click-modal="false">
      <el-form ref="taskForm" :model="taskForm" label-width="140px" :rules="formRules">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="任务名称" prop="task_name">
              <el-input v-model="taskForm.task_name" placeholder="请输入任务名称" size="large" />
            </el-form-item>

            <el-form-item label="账号" prop="video_account_ids">
              <div class="account-select-container">
                <el-select v-model="taskForm.video_account_ids" placeholder="请选择账号" size="large" multiple filterable
                  class="account-select">
                  <el-option v-for="account in videoAccountList" :key="account.id"
                    :label="account.account_name" :value="account.id">
                    <span>{{ account.account_name }}</span>
                    <span class="platform-tag" :style="{ backgroundColor: '#07C160' }">
                      视频号
                    </span>
                  </el-option>
                </el-select>

                <div class="selection-actions">
                  <div class="quick-selection">
                    <span class="action-label">快速选择：</span>
                    <el-button-group class="platform-buttons">
                      <el-button size="small" @click="selectAllAccounts" class="select-all-btn">
                        <el-icon><Select /></el-icon>
                        全选
                      </el-button>
                      <el-button size="small" @click="clearAllAccounts" class="clear-all-btn">
                        <el-icon>
                          <Close />
                        </el-icon>
                        清空
                      </el-button>
                    </el-button-group>
                  </div>
                </div>
              </div>
            </el-form-item>

            <el-form-item label="标题分组" prop="title_id">
              <el-select v-model="taskForm.title_id" placeholder="请选择标题分组" size="large" style="width: 100%" clearable
                filterable>
                <el-option v-for="item in titleList" :key="item.id" :label="item.title_name" :value="item.id" />
              </el-select>
            </el-form-item>

            <el-form-item label="描述分组" prop="desc_id">
              <el-select v-model="taskForm.desc_id" placeholder="请选择描述分组" size="large" style="width: 100%" clearable
                filterable>
                <el-option v-for="item in descList" :key="item.id" :label="item.desc_name" :value="item.id" />
              </el-select>
            </el-form-item>

            <el-form-item label="话题分组" prop="topic_id">
              <el-select v-model="taskForm.topic_id" placeholder="请选择话题分组" size="large" style="width: 100%" clearable
                filterable>
                <el-option v-for="item in topicList" :key="item.id" :label="item.topic_name" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item label="标题前缀">
              <el-input v-model="taskForm.add_prefix_to_title" placeholder="多个前缀用逗号分隔" size="large" />
            </el-form-item>

            <el-form-item label="标题后缀">
              <el-input v-model="taskForm.title_with_suffix" placeholder="多个后缀用逗号分隔" size="large" />
            </el-form-item>

            <el-form-item label="视频分组" prop="video_group_id">
              <el-select v-model="taskForm.video_group_id" placeholder="请选择视频分组" size="large" style="width: 100%" filterable>
                <el-option v-for="item in videoGroupList" :key="item.id" :label="item.video_name" :value="item.id" />
              </el-select>
            </el-form-item>

            <el-form-item label="挂载类型">
              <el-radio-group v-model="mountingType" size="large" @change="handleMountingTypeChange">
                <el-radio-button label="poi">地址挂载</el-radio-button>
                <el-radio-button label="none">无需挂载</el-radio-button>
              </el-radio-group>
            </el-form-item>

            <el-form-item v-if="mountingType === 'poi'" label="POI挂载" prop="poi_mounting_id">
              <el-select v-model="taskForm.poi_mounting_id" placeholder="请选择POI挂载" size="large" style="width: 100%"
                filterable clearable>
                <el-option v-for="item in poiMountingList" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider />

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="总发布数" prop="total_publications">
              <el-input-number v-model="taskForm.total_publications" :min="1" size="large" style="width: 100%" />
            </el-form-item>

            <el-form-item label="每日发布数" prop="daily_maximum_release">
              <el-input-number v-model="taskForm.daily_maximum_release" :min="1" size="large" style="width: 100%" />
            </el-form-item>

            <el-form-item label="执行间隔(秒)" prop="fb_interval">
              <el-input-number v-model="taskForm.fb_interval" :min="10" :max="86400" size="large" style="width: 100%" />
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item label="发布时间设置">
              <el-radio-group v-model="timeSettingType" size="large" @change="handleTimeSettingChange">
                <el-radio-button label="range">时间范围</el-radio-button>
                <el-radio-button label="specific">具体时间</el-radio-button>
              </el-radio-group>
            </el-form-item>

            <el-form-item v-if="timeSettingType === 'range'" label="开始时间" prop="api_release_time_start">
              <el-time-select v-model="taskForm.api_release_time_start" :max-time="taskForm.api_release_time_end"
                placeholder="开始时间" start="00:00" step="01:00" end="24:00" size="large" style="width: 100%" />
            </el-form-item>

            <el-form-item v-if="timeSettingType === 'range'" label="结束时间" prop="api_release_time_end">
              <el-time-select v-model="taskForm.api_release_time_end" :min-time="taskForm.api_release_time_start"
                placeholder="结束时间" start="00:00" step="01:00" end="24:00" size="large" style="width: 100%" />
            </el-form-item>

            <el-form-item v-if="timeSettingType === 'specific'" label="具体时间">
              <el-select v-model="taskForm.every_hour" multiple filterable allow-create default-first-option
                placeholder="请输入时间并回车添加，如 09:30" size="large" style="width: 100%">
                <el-option v-for="time in defaultTimes" :key="time" :label="time" :value="time" />
              </el-select>
            </el-form-item>

            <el-form-item label="发布算法" prop="article_publishing_algorithm">
              <el-radio-group v-model="taskForm.article_publishing_algorithm" size="large">
                <el-radio :label="1">仅发未发过的视频</el-radio>
                <el-radio :label="2">重复发送</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider />

        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="发布星期" prop="week_day">
              <el-checkbox-group v-model="taskForm.week_day" size="large">
                <el-checkbox-button v-for="day in weekDays" :key="day.value" :label="day.value">
                  {{ day.label }}
                </el-checkbox-button>
              </el-checkbox-group>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="备注信息">
          <el-input v-model="taskForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitTaskForm">确认</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
  Search, Plus, Edit, Delete
} from '@element-plus/icons-vue'
import { Select, Close } from '@element-plus/icons-vue'

export default {
  name: 'VideoPublishDraft',
  components: { Search, Plus, Edit, Delete, Select, Close },
  data() {
    return {
      loading: false,
      dialogVisible: false,
      dialogTitle: '新增任务',
      isEditMode: false,
      timeSettingType: 'range',
      mountingType: 'none',
      searchForm: {
        task_name: ''
      },
      taskForm: {
        id: null,
        task_name: '',
        video_account_ids: [],
        account_names: [],
        title_id: null,
        desc_id: null,
        topic_id: null,
        video_group_id: null,
        poi_mounting_id: null,
        week_day: [1, 2, 3, 4, 5, 6, 7],
        every_hour: [],
        add_prefix_to_title: '',
        title_with_suffix: '',
        total_publications: 100,
        daily_maximum_release: 1,
        fb_interval: 10,
        api_release_time_start: '00:00',
        api_release_time_end: '24:00',
        article_publishing_algorithm: 1,
        notes: ''
      },
      formRules: {
        task_name: [
          { required: true, message: '请输入任务名称', trigger: 'blur' }
        ],
        video_account_ids: [
          {
            required: true,
            message: '请至少选择一个账号',
            trigger: 'change',
            type: 'array',
            validator: (rule, value, callback) => {
              if (!value || value.length === 0) {
                callback(new Error('请至少选择一个账号'));
              } else {
                callback();
              }
            }
          }
        ],
        video_group_id: [
          { required: true, message: '请选择视频分组', trigger: 'change' }
        ],
        total_publications: [
          { required: true, message: '请输入总发布数', trigger: 'blur' }
        ],
        daily_maximum_release: [
          { required: true, message: '请输入每日发布数', trigger: 'blur' }
        ],
        fb_interval: [
          { required: true, message: '请输入执行间隔', trigger: 'blur' }
        ],
        api_release_time_start: [
          { required: true, message: '请选择开始时间', trigger: 'change' }
        ],
        api_release_time_end: [
          { required: true, message: '请选择结束时间', trigger: 'change' }
        ],
        article_publishing_algorithm: [
          { required: true, message: '请选择发布算法', trigger: 'change' }
        ],
        week_day: [
          { required: true, message: '请选择发布星期', trigger: 'change' }
        ]
      },
      taskList: [],
      videoAccountList: [],
      titleList: [],
      descList: [],
      topicList: [],
      videoGroupList: [],
      poiMountingList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      weekDays: [
        { label: '星期一', value: 1 },
        { label: '星期二', value: 2 },
        { label: '星期三', value: 3 },
        { label: '星期四', value: 4 },
        { label: '星期五', value: 5 },
        { label: '星期六', value: 6 },
        { label: '星期日', value: 7 }
      ],
      defaultTimes: [
        '08:00', '12:00', '18:00', '20:00', '22:00'
      ]
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    }
  },
  mounted() {
    this.fetchTaskList();
    this.fetchVideoAccountList();
    this.fetchTitleList();
    this.fetchDescList();
    this.fetchTopicList();
    this.fetchVideoGroupList();
    this.fetchPoiMountingList();
  },
  methods: {

    // 全选账号
    selectAllAccounts() {
      this.taskForm.video_account_ids = this.videoAccountList.map(account => account.id);
    },

    // 清空账号选择
    clearAllAccounts() {
      this.taskForm.video_account_ids = [];
    },

    // 获取账号名称
    getVideoAccountName(id) {
      const account = this.videoAccountList.find(item => item.id === id);
      return account ? account.account_name : '未知';
    },

    // 获取视频分组名称
    getVideoGroupName(id) {
      const group = this.videoGroupList.find(item => item.id === id);
      return group ? group.video_name : '未知';
    },

    // 格式化日期时间
    formatDateTime(dateString) {
      if (!dateString) return null;
      const date = new Date(dateString);
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        1: '待启动',
        2: '发布中',
        3: '发布完成',
        4: '发布失败'
      };
      return statusMap[status] || '未知状态';
    },

    // 获取状态标签类型
    getStatusTagType(status) {
      const typeMap = {
        1: 'info',
        2: 'warning',
        3: 'success',
        4: 'danger'
      };
      return typeMap[status] || '';
    },

    // 切换任务状态
    async toggleTaskStatus(row) {
      try {
        row.statusLoading = true;
        const newQdStatus = row.qd_status === 1 ? 2 : 1;

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/video_task_management.php`,
          {
            action: 'update_status',
            store_id: this.userInfo.id,
            id: row.id,
            qd_status: newQdStatus
          }
        );

        if (data.success) {
          this.$message.success('状态更新成功');
          row.qd_status = newQdStatus;
          row.status = newQdStatus === 1 ? 2 : 1;
        } else {
          this.$message.error(data.error || '状态更新失败');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '操作失败';
        this.$message.error(`操作失败: ${errorMsg}`);
        console.error('切换状态错误:', error);
      } finally {
        row.statusLoading = false;
      }
    },

    // 获取任务列表
    async fetchTaskList() {
      this.loading = true;
      try {
        const params = {
          action: this.searchForm.task_name ? 'search' : 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        if (this.searchForm.task_name) {
          params.task_name = this.searchForm.task_name;
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/video_task_management.php`,
          params
        );

        if (data.data) {
          this.taskList = data.data.map(task => ({
            ...task,
            statusLoading: false,
            week_day: task.week_day ? (typeof task.week_day === 'string' ? JSON.parse(task.week_day) : task.week_day) : [],
            every_hour: task.every_hour ? (typeof task.every_hour === 'string' ? JSON.parse(task.every_hour) : task.every_hour) : []
          }));
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) {
        this.$message.error('获取任务列表失败');
        console.error('获取任务列表错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 获取视频号账号列表
    async fetchVideoAccountList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/video_account_api.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.videoAccountList = data.data;
        }
      } catch (error) {
        console.error('获取账号列表错误:', error);
      }
    },

    // 获取标题分组列表
    async fetchTitleList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/title_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.titleList = data.data;
        }
      } catch (error) {
        console.error('获取标题分组列表错误:', error);
      }
    },

    // 获取描述分组列表
    async fetchDescList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/desc_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.descList = data.data;
        }
      } catch (error) {
        console.error('获取描述分组列表错误:', error);
      }
    },

    // 获取话题分组列表
    async fetchTopicList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/topic_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.topicList = data.data;
        }
      } catch (error) {
        console.error('获取话题分组列表错误:', error);
      }
    },

    // 获取视频分组列表
    async fetchVideoGroupList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/video_cp_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.videoGroupList = data.data;
        }
      } catch (error) {
        console.error('获取视频分组列表错误:', error);
      }
    },

    // 获取POI挂载列表
    async fetchPoiMountingList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/poi_mounting_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.poiMountingList = data.data;
        }
      } catch (error) {
        console.error('获取POI挂载列表错误:', error);
      }
    },

    // 处理搜索
    handleSearch() {
      this.pagination.page = 1;
      this.fetchTaskList();
    },

    // 打开新增对话框
    openAddDialog() {
      this.dialogTitle = '新增任务';
      this.isEditMode = false;
      this.timeSettingType = 'range';
      this.mountingType = 'none';
      this.taskForm = {
        id: null,
        task_name: '',
        video_account_ids: [],
        account_names: [],
        title_id: null,
        desc_id: null,
        topic_id: null,
        video_group_id: null,
        poi_mounting_id: null,
        week_day: [1, 2, 3, 4, 5, 6, 7],
        every_hour: [],
        add_prefix_to_title: '',
        title_with_suffix: '',
        total_publications: 100,
        daily_maximum_release: 1,
        fb_interval: 10,
        api_release_time_start: '00:00',
        api_release_time_end: '24:00',
        article_publishing_algorithm: 1,
        notes: ''
      };
      this.dialogVisible = true;
    },

    // 处理挂载类型变化
    handleMountingTypeChange(type) {
      if (type !== 'poi') this.taskForm.poi_mounting_id = null;
      if (type === 'none') {
        this.taskForm.poi_mounting_id = null;
      }
    },

    // 打开编辑对话框
    openEditDialog(row) {
      this.dialogTitle = '编辑任务';
      this.isEditMode = true;

      if (row.every_hour && row.every_hour.length > 0) {
        this.timeSettingType = 'specific';
      } else {
        this.timeSettingType = 'range';
      }

      this.mountingType = row.poi_mounting_id ? 'poi' : 'none';

      this.taskForm = {
        ...row,
        video_account_ids: row.video_account_ids ? (Array.isArray(row.video_account_ids) ? row.video_account_ids : [row.video_account_ids]) : [],
        week_day: row.week_day || [1, 2, 3, 4, 5, 6, 7],
        every_hour: row.every_hour || []
      };

      this.dialogVisible = true;
    },

    // 格式化时间 (HH:MM)
    formatTime(timeValue) {
      if (!timeValue) return '00:00';
      if (typeof timeValue === 'string' && /^\d+$/.test(timeValue)) {
        const num = parseInt(timeValue);
        if (num === 24) return '24:00';
        return `${num.toString().padStart(2, '0')}:00`;
      }
      if (typeof timeValue === 'number') {
        if (timeValue === 24) return '24:00';
        return `${timeValue.toString().padStart(2, '0')}:00`;
      }
      return timeValue;
    },

    // 处理时间设置类型变化
    handleTimeSettingChange(val) {
      if (val === 'range') {
        this.taskForm.every_hour = [];
      } else {
        this.taskForm.api_release_time_start = '00:00';
        this.taskForm.api_release_time_end = '24:00';
      }
    },

    // 提交表单（新增/编辑）
    submitTaskForm() {
      this.$refs.taskForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            
            // 获取选中的账号名称
            const accountNames = this.taskForm.video_account_ids.map(id => {
              const account = this.videoAccountList.find(a => a.id === id);
              return account ? account.account_name : '';
            }).filter(name => name);
            
            const payload = {
              ...this.taskForm,
              store_id: this.userInfo.id,
              account_names: accountNames,
              week_day: JSON.stringify(this.taskForm.week_day)
            };

            // 处理时间设置
            if (this.timeSettingType === 'range') {
              payload.every_hour = '';
              // 转换时间格式为数字
              const startHour = parseInt(payload.api_release_time_start.split(':')[0]);
              const endHour = payload.api_release_time_end === '00:00' ? 24 : parseInt(payload.api_release_time_end.split(':')[0]);
              payload.api_release_time_start = startHour;
              payload.api_release_time_end = endHour;
            } else {
              // 具体时间模式
              payload.api_release_time_start = 0;
              payload.api_release_time_end = 0;
              payload.every_hour = JSON.stringify(payload.every_hour);
            }

            const { data } = await axios.post(
              `${config.BASE_URL}/shop/api/video_task_management.php`,
              {
                action,
                ...payload
              }
            );

            if (data.success) {
              this.$message.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchTaskList();
            } else {
              this.$message.error(data.error || '操作失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '操作失败';
            this.$message.error(`操作失败: ${errorMsg}`);
            console.error('表单提交错误:', error);
          }
        }
      });
    },

    // 删除任务
    async handleDelete(id) {
      this.$confirm('确定要删除该任务吗？', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/video_task_management.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.id
            }
          );

          if (data.success) {
            this.$message.success('删除成功');
            this.fetchTaskList();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }).catch(() => { });
    }
  }
}
</script>

<style scoped lang="scss">
.video-task-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.page-title {
  font-size: 24px;
  color: #1f2937;
  margin-bottom: 32px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.5px;
}

.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  flex-wrap: wrap;
  gap: 10px;

  .add-btn {
    margin-left: auto;
  }
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
  -webkit-overflow-scrolling: touch;
}

.task-table {
  :deep(.el-table__header) th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
    white-space: nowrap;
  }

  :deep(.el-table__body) tr:hover>td {
    background-color: #f0f7ff !important;
  }
  
  :deep(.el-table__body-wrapper) {
    overflow-x: auto !important;
    
    &::-webkit-scrollbar {
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 4px;

      &:hover {
        background: #a8a8a8;
      }
    }
  }
  
  :deep(.el-table__header-wrapper) {
    overflow: hidden !important;
    width: 100% !important;
  }
}

/* 操作按钮样式 */
.el-button {
  display: inline-flex;
  align-items: center;

  .el-icon {
    margin-right: 5px;
  }
}

/* 对话框样式优化 */
:deep(.el-dialog) {
  border-radius: 12px;

  .el-dialog__header {
    padding: 20px 24px;
    border-bottom: 1px solid #ebeef5;
    margin-right: 0;

    .el-dialog__title {
      font-weight: 600;
      color: #1f2937;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #ebeef5;
  }
}

/* 表单验证提示样式 */
:deep(.el-form-item__error) {
  font-size: 12px;
  padding-top: 4px;
}

.el-divider {
  margin: 20px 0;
}

/* 星期选择样式 */
:deep(.el-checkbox-group) {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

:deep(.el-checkbox-button) {
  margin-right: 0;
}

:deep(.el-checkbox-button__inner) {
  border-left: 1px solid #dcdfe6;
  border-radius: 4px;
}

/* 账号选择相关样式 */
.account-select-container {
  width: 100%;
}

.account-select {
  width: 100%;
  margin-bottom: 12px;
}

.selection-actions {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  margin-top: 8px;
}

.quick-selection {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.action-label {
  font-size: 12px;
  color: #606266;
  margin-right: 10px;
  white-space: nowrap;
  line-height: 28px;
  flex-shrink: 0;
}

.platform-buttons {
  margin-right: 15px;
}

.select-all-btn {
  background: linear-gradient(135deg, #409eff, #337ecc);
  border-color: #409eff;
  color: white;

  &:hover {
    background: linear-gradient(135deg, #66b1ff, #409eff);
    border-color: #66b1ff;
  }
}

.clear-all-btn {
  background: linear-gradient(135deg, #f56c6c, #dd6161);
  border-color: #f56c6c;
  color: white;

  &:hover {
    background: linear-gradient(135deg, #f78989, #f56c6c);
    border-color: #f78989;
  }
}

.platform-tag {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  color: white;
  margin-left: 6px;
  background-color: #07C160;
}

/* 下拉选项中的平台标签样式 */
:deep(.el-select-dropdown__item) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
}

:deep(.el-select-dropdown__item .platform-tag) {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  color: white;
  margin-left: 6px;
}

:deep(.el-tag) {
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
}

/* 响应式优化 */
@media (max-width: 768px) {
  .search-bar {
    flex-direction: column;
    align-items: stretch;
    
    .el-input {
      width: 100% !important;
      margin-right: 0 !important;
    }
    
    .add-btn {
      margin-left: 0;
    }
  }
  
  .task-table {
    font-size: 12px;
  }

  .quick-selection {
    flex-direction: column;
    align-items: flex-start;
  }

  .action-label {
    margin-bottom: 5px;
  }
}
</style>